import type { ColumnDef } from "@tanstack/react-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { formatDate } from "@/shared/utilities/date";
import { SampleSetNumber } from "@/shared/components/domain/sample-set/sample-set-number";
import { CollapsibleDataTable } from "@/shared/components/collapsible-data-table";
import { SampleTypeComponent } from "@/shared/components/domain/sample-set/sample-type-component";
import { useSampleSetListQuery } from "@/services/backend/sample-set/service";
import { useEffect, useState } from "react";
import { SampleDocumentationViewModel } from "@/services/backend/sample-set/sample-set";
import { GrowthMedium } from "@/services/backend/types";
import SampleMetadataRow from "./sample-metadata-row";

const sampleType: { [key: string]: string } = {
  [GrowthMedium.Caso]: "Caso",
  [GrowthMedium.Malt]: "Malz",
  [GrowthMedium.DG18]: "DG18",
};

const tableColumns: ColumnDef<SampleDocumentationViewModel>[] = [
  {
    id: "id",
    accessorKey: "sampleSetId",
    header: "Probenset",
    cell: ({ row }) => {
      const sampleData = row.original;
      return <SampleSetNumber sampleSetId={sampleData.set.id} />;
    },
  },
  {
    id: "sampleType",
    accessorKey: "sampleSetId",
    header: "Probentyp",
    cell: ({ row }) => {
      const sampleData = row.original;
      return <SampleTypeComponent sampleSetId={sampleData.set.id} />;
    },
  },
  {
    id: "growthMedium",
    accessorKey: "growthMedium",
    header: "Nährmedium",
    cell: ({ row }) => {
      const sampleData = row.original;
      return <div>{sampleType[sampleData.sample.growthMedium]}</div>;
    },
  },
  {
    id: "creationDate",
    accessorKey: "creationDate",
    header: "Beginn der Probenahme",
    cell: ({ row }) => {
      const sampleData = row.original;
      return (
        <div className="items-start">
          <div className="flex flex-col items-start">
            <span className="font-bold">
              {formatDate(sampleData.sample.createdAt)}
            </span>
          </div>
        </div>
      );
    },
  },
];

interface SampleTableProps {
  ownerId: string;
  ownerType: string;
}

export function SampleTable({ ownerId, ownerType }: SampleTableProps) {
  const [samples, setSamples] = useState<SampleDocumentationViewModel[]>([]);
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useSampleSetListQuery({
    ownerId: {
      active: true,
      values: [ownerId],
    },
    ownerType: {
      active: true,
      values: [ownerType],
    },
    limit: 0,
    offset: 0,
  });

  useEffect(() => {
    const sampleList: SampleDocumentationViewModel[] = [];
    if (list != null && list.count > 0) {
      list.items.forEach((set) => {
        set.samples.forEach((sample) => {
          sampleList.push({ set, sample });
        });
      });
    }
    setSamples(sampleList);
  }, [list]);

  const table = useDataTableController(tableColumns, samples);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <DataTableContainer>
      <CollapsibleDataTable
        table={table}
        columns={tableColumns}
        renderCollapsibleContent={(vm: SampleDocumentationViewModel) => (
          <SampleMetadataRow sampleSetId={vm.set.id} sample={vm.sample} />
        )}
        collapsibleTriggerId="id"
        isLoading={isLoading || isFetching}
      />
    </DataTableContainer>
  );
}
